import React from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  Divider,
  Box,
  Chip
} from "@mui/material"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import type { OrderRow } from "@/features/profit-calculators/calculator-shared/types"

interface OrderBreakdownModalProps {
  open: boolean
  onClose: () => void
  order: OrderRow | undefined
  orderId: string
  sku: string
  productName: string
}

const fmt = (n: number) =>
  `₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const OrderBreakdownModal: React.FC<OrderBreakdownModalProps> = ({
  open,
  onClose,
  order,
  orderId,
  sku,
  productName
}) => {
  if (!order) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Order Component Ledger</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 4 }}>
            No payment transaction ledger found for this order. It might be cancelled or pending settlement.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  // 18 components list
  // Each has: Label, Key in OrderRow, Type (addition/subtraction)
  const componentsList: { label: string; val: number; type: "add" | "sub" }[] = [
    { label: "Sale Amount", val: order.totalSaleAmount, type: "add" },
    {
      label: "Sale Return Amount",
      val: order.totalSaleReturnAmount,
      type: "sub"
    },
    { label: "Meesho Commission", val: order.meeshoCommission, type: "sub" },
    {
      label: "Return Shipping Charge",
      val: order.returnShippingCharge,
      type: "sub"
    },
    { label: "Shipping Charge", val: order.shippingCharge, type: "sub" },
    {
      label: "Net Other Support Service Charges",
      val: order.netOtherSupportCharges,
      type: "sub"
    },
    { label: "TCS", val: order.tcs, type: "sub" },
    { label: "Compensation", val: order.compensation, type: "add" },
    { label: "Recovery", val: order.recovery, type: "sub" },
    { label: "TDS", val: order.tds, type: "sub" },
    { label: "Return Premium", val: order.returnPremium, type: "add" },
    {
      label: "Return Premium of Return",
      val: order.returnPremiumOfReturn,
      type: "sub"
    },
    { label: "GST Compensation", val: order.gstCompensation, type: "add" },
    { label: "Claims", val: order.claims, type: "add" },
    {
      label: "Meesho Mall Platform Fee",
      val: order.meeshoMallPlatformFee,
      type: "sub"
    },
    {
      label: "Fixed Fee",
      val: order.fixedFeeDeduction || order.fixedFeeRevenue,
      type: "sub"
    },
    {
      label: "Warehousing Fee",
      val: order.warehousingFeeDeduction || order.warehousingFeeRevenue,
      type: "sub"
    },
    {
      label: "Meesho Gold Platform Fee",
      val: order.meeshoGoldPlatformFee,
      type: "sub"
    }
  ]

  // Filter components that have non-zero value to show a clean ledger
  const activeComponents = componentsList.filter(c => Math.abs(c.val) > 0.001)

  // Double check math
  const formulaSettlement = componentsList.reduce((acc, curr) => {
    // If it's a subtraction component, we subtract it from the total. If it is already negative, we subtract its absolute.
    const val = Math.abs(curr.val)
    return curr.type === "add" ? acc + val : acc - val
  }, 0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              Meesho Payment Transaction Ledger
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              Order ID: {orderId} | SKU: {sku}
            </Typography>
          </Stack>
          <Chip
            icon={<AccountBalanceWalletIcon />}
            label={`Settlement: ${fmt(order.finalSettlementAmount)}`}
            color="success"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Product Name:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {productName}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "background.neutral" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Component Name</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>
                      Effect
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, width: 150 }}>
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeComponents.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{c.label}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={c.type === "add" ? "+" : "−"}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            bgcolor: c.type === "add" ? "success.shading" : "error.shading",
                            color: c.type === "add" ? "success.main" : "error.main",
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={c.type === "add" ? "success.main" : "error.main"}>
                          {c.type === "add" ? "" : "−"}
                          {fmt(c.val)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                  {activeComponents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No active payment ledger components found (all components are 0)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals Summary */}
            <Box
              sx={{
                p: 2,
                bgcolor: "background.neutral",
                borderTop: 1,
                borderColor: "divider"
              }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Calculated Ledger Total:
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {fmt(formulaSettlement)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Actual Settlement Disbursed:
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    {fmt(order.finalSettlementAmount)}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" align="center" display="block">
                  Formula: SaleAmount + Compensation + Referral + Claims + Premium + GSTComp − (ReturnShipping +
                  Commission + Shipping + OtherCharges + TCS + TDS + Fees + Recoveries)
                </Typography>
              </Stack>
            </Box>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2 }}>
          Close Breakdown
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default OrderBreakdownModal
